/**
 * Loading Service - Zentrales Fortschritts-Tracking für App-Initialisierung
 * 
 * Verwaltet Lade-Phasen:
 * 1. Cache Hydration (IndexedDB) - 20%
 * 2. Product List - 30%
 * 3. Minimal Cell Data - 30%
 * 4. Global Stats - 20%
 * 
 * Events: progress, phase-change, complete, error
 */

const listeners = new Map() // eventType -> Set of callbacks

// Loading phases with weight (totals 100%)
const PHASES = {
  CACHE_HYDRATION: { weight: 20, label: 'Loading cached data...' },
  PRODUCT_LIST: { weight: 30, label: 'Fetching cell list...' },
  MINIMAL_DATA: { weight: 30, label: 'Loading cell details...' },
  GLOBAL_STATS: { weight: 20, label: 'Computing statistics...' }
}

let currentPhase = null
let phaseProgress = 0 // 0-100 within current phase
let totalProgress = 0 // 0-100 overall
let isComplete = false
let hasError = false

/**
 * Subscribe to loading events
 * @param {string} eventType - 'progress', 'phase-change', 'complete', 'error'
 * @param {function} callback - Called with event data
 * @returns {function} Unsubscribe function
 */
export function onLoadingEvent(eventType, callback) {
  if (!listeners.has(eventType)) {
    listeners.set(eventType, new Set())
  }
  listeners.get(eventType).add(callback)
  
  // Return unsubscribe function
  return () => {
    listeners.get(eventType)?.delete(callback)
  }
}

/**
 * Emit event to all listeners
 */
function emit(eventType, data) {
  const callbacks = listeners.get(eventType)
  if (callbacks) {
    callbacks.forEach(cb => cb(data))
  }
}

/**
 * Start a new loading phase
 * @param {string} phase - Phase key from PHASES
 */
export function startPhase(phase) {
  if (!PHASES[phase]) {
    console.warn(`[LoadingService] Unknown phase: ${phase}`)
    return
  }
  
  currentPhase = phase
  phaseProgress = 0
  
  emit('phase-change', {
    phase,
    label: PHASES[phase].label,
    totalProgress
  })
  
  console.log(`[LoadingService] Phase started: ${phase} (${PHASES[phase].label})`)
}

/**
 * Update progress within current phase
 * @param {string} phase - Phase key (for validation)
 * @param {number} progress - 0-1 (0-100%) within current phase
 */
export function updatePhaseProgress(phase, progress) {
  if (!currentPhase || currentPhase !== phase) {
    console.warn(`[LoadingService] Phase mismatch: expected ${currentPhase}, got ${phase}`)
    return
  }
  
  phaseProgress = Math.max(0, Math.min(100, progress * 100))
  
  // Calculate total progress based on phase weights
  let accumulatedWeight = 0
  for (const [key, config] of Object.entries(PHASES)) {
    if (key === currentPhase) {
      // Current phase: add partial progress
      totalProgress = accumulatedWeight + (phaseProgress / 100) * config.weight
      break
    } else {
      // Previous phases: add full weight
      accumulatedWeight += config.weight
    }
  }
  
  emit('progress', {
    phase: currentPhase,
    phaseProgress,
    totalProgress: Math.round(totalProgress),
    label: PHASES[currentPhase].label
  })
}

/**
 * Complete current phase (sets progress to 100%)
 * @param {string} phase - Phase key (for validation)
 */
export function completePhase(phase) {
  if (!currentPhase || currentPhase !== phase) {
    console.warn(`[LoadingService] Phase mismatch: expected ${currentPhase}, got ${phase}`)
    return
  }
  
  updatePhaseProgress(phase, 1.0)
  
  console.log(`[LoadingService] Phase completed: ${currentPhase}`)
  
  // Don't auto-complete - let appInitializer call markComplete()
}

/**
 * Mark entire loading process as complete
 */
export function markComplete() {
  if (isComplete) return
  
  isComplete = true
  totalProgress = 100
  
  emit('complete', {
    totalProgress: 100
  })
  
  console.log('[LoadingService] ✅ All loading complete')
}

/**
 * Report an error during loading
 * @param {string} phase - Phase where error occurred
 * @param {Error} error - The error object
 */
export function reportError(phase, error) {
  hasError = true
  
  emit('error', {
    phase,
    error,
    message: error.message || 'Unknown error'
  })
  
  console.error(`[LoadingService] ❌ Error in ${phase}:`, error)
}

/**
 * Reset loading state (for testing/re-initialization)
 */
export function reset() {
  currentPhase = null
  phaseProgress = 0
  totalProgress = 0
  isComplete = false
  hasError = false
  listeners.clear()
  
  console.log('[LoadingService] Reset')
}

/**
 * Get current loading state
 */
export function getState() {
  return {
    currentPhase,
    phaseProgress,
    totalProgress,
    isComplete,
    hasError,
    currentLabel: currentPhase ? PHASES[currentPhase].label : null
  }
}

// Debug helper
if (typeof window !== 'undefined') {
  window.__LOADING_SERVICE__ = {
    startPhase,
    updatePhaseProgress,
    completePhase,
    markComplete,
    reportError,
    reset,
    getState,
    onLoadingEvent
  }
  console.log('[LoadingService] 💡 Debug tools: window.__LOADING_SERVICE__')
}

// Convenience helpers for common event subscriptions
export const onProgress = (callback) => onLoadingEvent('progress', callback)
export const onPhaseChange = (callback) => onLoadingEvent('phase-change', callback)
export const onComplete = (callback) => onLoadingEvent('complete', callback)
export const onError = (callback) => onLoadingEvent('error', callback)
